import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { translate } from '@optimusgps/optimus-intl';
import { Card, Empty, message, Steps } from 'antd';
import { formatDate } from '@/utilities/dateUtils';
import { EmailLogEventHistoryResponseDto } from '../diagnostics.dto';
import DiagnosticsService from '../diagnostics.service';

const { Step } = Steps;

type EventHistoryProps = {
	providerMessageId: string;
	recipientEmail: string;
};

type EventHistoryState = {
	data: EmailLogEventHistoryResponseDto[];
	isLoading: boolean;
};

export const EventHistory = ({ providerMessageId, recipientEmail }: EventHistoryProps) => {
	const intl = useIntl();
	const [eventHistory, setEventHistory] = useState<EventHistoryState>({ data: [], isLoading: false });

	const fetchEventHistory = async () => {
		try {
			setEventHistory((prev) => ({ ...prev, isLoading: true }));
			const result = await DiagnosticsService.getEmailLogEventHistory({
				providerMessageId,
				recipientEmail,
			});

			if (result.error || !result.success) {
				message.error({
					content: intl.formatMessage(translate('commonError')),
					key: 'commonError',
				});
				return;
			}

			setEventHistory((prev) => ({ ...prev, data: result.success || [] }));
		} catch {
			message.error({
				content: intl.formatMessage(translate('commonError')),
				key: 'commonError',
			});
		} finally {
			setEventHistory((prev) => ({ ...prev, isLoading: false }));
		}
	};

	useEffect(() => {
		fetchEventHistory();
	}, [providerMessageId, recipientEmail]);

	const hasEvents = eventHistory.data?.length > 0;

	const renderContent = () => {
		if (!hasEvents) {
			return <Empty description={intl.formatMessage(translate('noData'))} />;
		}

		return (
			<Steps direction="horizontal" size="small" current={eventHistory.data.length - 1} status="finish">
				{eventHistory.data.map((event, index) => (
					<Step
						key={index}
						title={`${intl.formatMessage(translate(event.status?.toLowerCase()))} (${event.occurrence})`}
						description={formatDate('M/D/YYYY HH:mm:ss', event.eventAt, true) || '-'}
						status={event.occurrence > 0 ? 'finish' : 'wait'}
					/>
				))}
			</Steps>
		);
	};

	return (
		<Card title={intl.formatMessage(translate('eventsHistory'))} loading={eventHistory.isLoading}>
			{renderContent()}
		</Card>
	);
};
